import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/types/api.types';

/**
 * Shared TanStack Query client. Retries are deliberately conservative: portal-backend already applies a
 * per-domain Polly pipeline upstream, so retrying again in the browser would multiply load on the BF APIs.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        // Answers are kept well past the point they stop being fresh (PP-028 / proweb-74 AC-003): a
        // repeated lookup inside the window costs no call at all, and when the backend goes away the
        // figures already retrieved stay on screen under the outage banner instead of blanking out.
        gcTime: 30 * 60_000,
        refetchOnWindowFocus: false,
        // Attempt the call and report what happens, rather than parking it (PP-028 / proweb-74).
        // The default mode suspends a query whenever the browser reports itself offline: the request
        // is never made, the query never reaches its error state, and the screen keeps a loading
        // skeleton up indefinitely — which is the one outcome a story about telling the user the
        // service is unreachable cannot have. Observed live as fetchStatus 'paused' with the backend
        // stopped. Failing takes us to the standardised banner; pausing takes us nowhere.
        networkMode: 'always',
        retry: (failureCount, error): boolean => {
          // 4xx responses are final: a rejected verification or a not-found policy will not succeed
          // on a second attempt.
          if (error instanceof ApiError && error.status !== undefined && error.status < 500) {
            return false;
          }

          return failureCount < 1;
        },
      },
      mutations: { retry: false, networkMode: 'always' },
    },
  });
}
